/* eslint-disable */

import Cookies from 'js-cookie';
import camelCase from 'lodash/camelCase';
import fromPairs from 'lodash/fromPairs';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import toPairs from 'lodash/toPairs';
import transform from 'lodash/transform';
import moment from 'moment';
import { DateRange } from './../data/DateRangeOptions';
import Analytics from './Analytics';
import Communication from './Communication';
import Session from './Session';

/**
 * Reset-password credentials (`username`, `token`) taken out of the URL by the
 * inline script in Account/src/index.html before third-party scripts run.
 * Raw (still URL-encoded) values, held in module memory only.
 */
const heldCredentials =
	(typeof window.__takeResetCredentials === 'function' &&
		window.__takeResetCredentials()) ||
	{};

class Util {
	/*
	 * Randomizes the order of array by walking the array in the reverse order
	 * and swap each element with a random one before it.
	 * @param {array} array to shuffle
	 * @param {shuffleLastValue} boolean value that is used to exclude/include the last value in the array from the shuffling
	 */
	static shuffle = (array, shuffleLastValue) => {
		let i = shuffleLastValue ? array.length - 2 : array.length - 1;
		let j;

		for (i - 1; i > 0; i--) {
			j = Math.floor(Math.random() * (i + 1));
			[array[i], array[j]] = [array[j], array[i]];
		}
		return array;
	};

	static convertEnumToObjectArray = obj => {
		const newObjArray = [];

		Object.keys(obj).forEach(key => {
			newObjArray.push({
				key,
				label: obj[key],
			});
		});

		return newObjArray;
	};

	// formats phone numbers
	static formatPhoneText = value => {
		// return nothing if no value
		if (!value) return value;

		// only allows 0-9 inputs
		const currentValue = value.replace(/[^\d]/g, '');

		// returns: "x", "xx", "xxx"
		if (currentValue.length <= 3) {
			return currentValue;
		}

		// returns: "(xxx)"
		if (currentValue.length === 3) {
			return `(${currentValue})`;
		}

		// returns: "(xxx) x", "(xxx) xx", "(xxx) xxx",
		if (currentValue.length <= 6) {
			return `(${currentValue.slice(0, 3)}) ${currentValue.slice(3)}`;
		}

		// returns: "(xxx) xxx-"
		if (currentValue.length === 6) {
			return `(${currentValue.slice(0, 3)}) ${currentValue.slice(3)}-`;
		}

		// returns: "(xxx) xxx-x", "(xxx) xxx-xx", "(xxx) xxx-xxx", "(xxx) xxx-xxxx"
		return `(${currentValue.slice(0, 3)}) ${currentValue.slice(3, 6)}-${currentValue.slice(
			6,
			10
		)}`;
	};

	// gets today's date to set for calendar maxDate attribute
	static getMaxDate = () => {
		const time = new Date();
		const today = time.getDate();
		const maxDate = new Date();
		maxDate.setDate(today);
		return maxDate;
	};

	/**
	 * Covert ISO 8601 date time string with Timezone Offset to local date object.
	 * @{string} isoDateTimeString e.g. 2020-06-11T10:27:12.347
	 * @returns {Date|null}
	 */
	static convertIsoDateTimeStringToDate = isoDateTimeString => {
		const timeZoneOffset = new Date().getTimezoneOffset() * 60000;
		const matched = isoDateTimeString ? isoDateTimeString.match(/\d+/g) : null;
		const isoTime = isArray(matched)
			? Date.UTC(
					+matched[0],
					+matched[1] - 1,
					+matched[2],
					+matched[3],
					+matched[4],
					+matched[5]
				)
			: null;

		return isoTime ? new Date(isoTime + timeZoneOffset) : null;
	};

	// converts selected date object to string for calendar select
	static convertDate = date => {
		const stringDate = date.toString();
		const splitDate = stringDate.split(' ');
		const months = {
			Jan: '01',
			Feb: '02',
			Mar: '03',
			Apr: '04',
			May: '05',
			Jun: '06',
			Jul: '07',
			Aug: '08',
			Sep: '09',
			Oct: '10',
			Nov: '11',
			Dec: '12',
		};
		return `${months[splitDate[1]]}/${splitDate[2]}/${splitDate[3]}`;
	};

	// format date to YYYY-MM-DD from MM/DD/YYYY
	static formatDate = date => {
		return moment(date).format('YYYY-MM-DD');
	};

	// format date to readable format (e.g. "January 15, 2024")
	static formatDateReadable = date => {
		const dateObj = new Date(date);
		return dateObj.toLocaleDateString(undefined, {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		});
	};

	// formats calendar MM/DD/YYYY
	static formatCalendar = value => {
		let date = value;
		// return nothing if no value
		if (!value) return value;

		if (typeof value === 'object') {
			date = this.convertDate(value);
		}

		// only allows 0-9 inputs
		const currentValue = date.replace(/[^\d]/g, '');

		// returns: "M" or "MM"
		if (currentValue.length <= 2) {
			return currentValue;
		}

		// returns: "MM/"
		if (currentValue.length === 2) {
			return `${currentValue}/`;
		}

		// returns: "MM/D", "MM/DD"
		if (currentValue.length <= 4) {
			return `${currentValue.slice(0, 2)}/${currentValue.slice(2)}`;
		}

		// returns: "MM/DD/"
		if (currentValue.length === 4) {
			return `${currentValue.slice(0, 2)}/${currentValue.slice(2)}/`;
		}

		// returns: "MM/DD/Y", "MM/DD/YY", "MM/DD/YYY", "MM/DD/YYYY"
		return `${currentValue.slice(0, 2)}/${currentValue.slice(2, 4)}/${currentValue.slice(
			4,
			10
		)}`;
	};

	static formatZip = zipValue => {
		// return nothing if no value
		if (!zipValue) return zipValue;

		// only allows 0-9 inputs
		const currentValue = zipValue.replace(/[^\d]/g, '');

		// returns: "x", "xx", "xxx", "xxxx", "xxxx", "xxxxx"
		if (currentValue.length <= 5) {
			return currentValue;
		}

		// returns: "xxxxx-xxxx"
		return `${currentValue.slice(0, 5)}-${currentValue.slice(5, 10)}`;
	};

	// returns the next month(1 -> 2), if month is 12 will return 1
	static getNextMonth = () => ((new Date().getMonth() + 1) % 12) + 1;

	// returns the following year if the current month is 12, else returns current year
	static getYearForNextMonth = () => {
		if (new Date().getMonth() + 1 === 12) {
			return (new Date().getFullYear() + 1).toString().substr(2, 2);
		} else {
			return new Date().getFullYear().toString().substr(2, 2);
		}
	};

	// get the next billing date for user in *monthName day, fullYear* format (e.g. April 14, 2020)
	// if the current day is past the billingDay, the function returns the next month
	static getBillingDate = (billingDay, type = 'numeric') => {
		const currentDay = new Date();
		const currentYear = currentDay.getFullYear();
		const dayOfMonth = currentDay.toLocaleString('en-us', { month: type });
		const nextMonth = (currentDay.getMonth() + 1) % 12;
		const nextDayOfMonth = new Date(
			currentDay.setMonth(nextMonth)
		).toLocaleString('en-us', {
			month: type,
		});
		const nextBillingYear =
			currentDay.getMonth() + 1 === 12
				? currentDay.getFullYear() + 1
				: currentDay.getFullYear();

		if (billingDay > currentDay.getDate()) {
			if (type === 'numeric') {
				return `${dayOfMonth}/${billingDay}/${currentYear}`;
			}
			return `${dayOfMonth} ${billingDay}, ${currentYear}`;
		}

		if (type === 'numeric') {
			return `${nextDayOfMonth}/${billingDay}/${nextBillingYear}`;
		}
		return `${nextDayOfMonth} ${billingDay}, ${nextBillingYear}`;
	};

	/**
	 * Returns the next day.
	 * @param {string} date The current date in MM/DD/YYYY format.
	 * @returns {string} The next day in MM/DD/YYYY format.
	 */
	static getNextDay = date => {
		return moment(date).add(1, 'days').format('MM/DD/YYYY');
	};

	static setCookie = (name, value, expiration, path = '/') => {
		const expires = expiration === '1 minute' ? 1 / (24 * 60) : expiration;

		Cookies.set(name, value, { expires, path, domain: '.stamps.com' });
	};

	static getCookie = name => Cookies.get(name);

	static removeCookie = (name, path) => {
		Cookies.remove(name, { path, domain: '.stamps.com' });
	};

	/**
	 * Retrieve Visitor cookie value.
	 * If one does not exist, it will set one and return the value.
	 * @returns {string}
	 */
	static getVisitor = () => {
		const visitorCookie = this.getCookie('Visitor');

		return isString(visitorCookie) ? visitorCookie : this.setVisitor();
	};

	/**
	 * Generate a random visitor id, create a Visitor cookie and return it.
	 * The logic to generate visitor id is the same as the one we have in
	 * legacy apps.
	 * @returns {string}
	 */
	static setVisitor = () => {
		// generate a random number from 0 and < 100
		const randomNumber = Math.floor(Math.random() * 100);

		// generate a random 15-digit visitor id
		const randomVisitorId = [
			new Date().getTime(),
			randomNumber < 10 ? `0${randomNumber}` : randomNumber,
		].join('');

		// create a Visitor cookie
		Cookies.set('Visitor', randomVisitorId, {
			expires: 3650, // 10-year expiration date
			path: '/',
			domain: '.stamps.com',
		});

		return randomVisitorId;
	};

	static hideElementsForPrinting = () => {
		document.querySelector('#root').classList.add('hide-for-printing');
		document.querySelector('#modals').classList.add('hide-for-printing');
	};

	static showElementsHiddenForPrinting = () => {
		document.querySelector('#root').classList.remove('hide-for-printing');
		document.querySelector('#modals').classList.remove('hide-for-printing');
	};

	static printContentId = 'printContent';

	static addPrintContent = config => {
		const { content, id } = config;
		const contentDiv = document.createElement('div');
		const printContentContainer = document.getElementById(this.printContentId);
		contentDiv.id = id;
		contentDiv.innerHTML = content;

		this.hideElementsForPrinting();

		printContentContainer.appendChild(contentDiv);
	};

	static removePrintContent = id => {
		const printContent = document.querySelector(
			`#${this.printContentId} #${id}`
		);
		printContent.parentNode.removeChild(printContent);

		this.showElementsHiddenForPrinting();
	};

	/**
	 * Get a query parameter value by name.
	 * @param {string} paramName - The query parameter name (case insensitive).
	 * @param {object} [options] - Optional settings.
	 * @param {boolean} [options.preservePlus=false] - If true, do not decode '+' as space.
	 *   Use for opaque values (e.g. reset password token) that may contain literal '+'.
	 * @returns {string|null} The decoded value or null if not present.
	 */
	static getQueryParamByName = (paramName, options = {}) => {
		const { preservePlus = false } = options;
		// per WEBAPPS-25671, make the search case insensitive
		const match = RegExp(`[?&]${paramName}=([^&]*)`, 'i').exec(
			window.location.search
		);
		const raw = match ? match[1] : heldCredentials[paramName.toLowerCase()];

		if (raw === undefined) return null;
		const decoded = preservePlus
			? decodeURIComponent(raw)
			: decodeURIComponent(raw.replace(/\+/g, ' '));
		return decoded;
	};

	/**
	 * Hold reset-password credentials in memory (never in the URL) so they can be
	 * read with getQueryParamByName.
	 * @param {{username?: string, token?: string}} credentials Decoded values.
	 */
	static holdResetCredentials = ({ username, token }) => {
		if (username !== undefined) {
			heldCredentials.username = encodeURIComponent(username);
		}
		if (token !== undefined) {
			heldCredentials.token = encodeURIComponent(token);
		}
	};

	/**
	 * Remove one or more params from the query string.
	 * To remove one param, pass a param name.
	 * To remove more than one param, pass an array of params.
	 * @param {string|array} params The params to be removed from the query string.
	 */
	static removeQueryParamByName = params => {
		// held (out-of-URL) reset credentials are "removed" too
		[].concat(params).forEach(param => {
			delete heldCredentials[String(param).toLowerCase()];
		});

		// get the location href without the query string
		const { href } = window.location;
		const url = href.substr(0, href.indexOf('?'));

		// find all query params in the query string
		const querystring = href.substr(href.indexOf('?'));
		const match = querystring.match(/\w+=([^&]*)/gi);

		if (match) {
			// regular expression for the paramName
			const regExp = isArray(params)
				? RegExp(params.join('|'), 'i')
				: RegExp(params, 'i');

			// remove the param and build a new query string
			const newQueryString = match
				.filter(param => !regExp.test(param))
				.join('&');

			window.history.replaceState(
				{},
				document.title,
				`${newQueryString ? `${url}?${newQueryString}` : url}`
			);
		}
	};

	static getPhoneNumber = number => {
		const rawPhoneNumber = Util.removePhoneFormatting(number);
		return rawPhoneNumber.substring(0, 10);
	};

	static getExtension = number => {
		const rawPhoneNumber = Util.removePhoneFormatting(number);
		return rawPhoneNumber.length > 10 ? rawPhoneNumber.slice(10) : null;
	};

	static removePhoneFormatting = number =>
		isString(number) && number.length > 0 ? number.replace(/[^\d]/g, '') : '';

	static maskPhoneNumber = number => {
		const phoneNumber = Util.getPhoneNumber(number),
			extension = Util.getExtension(number);
		let maskedPhoneNumber = `XXX-XXX-${phoneNumber.slice(-4)}`;
		return (maskedPhoneNumber += extension ? ` ext:${extension}` : '');
	};

	static maskEmail = email => {
		const [username, domain] = email.split('@');
		let maskedUsername = '';

		if (username.length <= 3) {
			maskedUsername = `${'*'.repeat(username.length)}`;
		} else {
			maskedUsername = `${username.substring(0, 3)}${'*'.repeat(username.length - 3)}`;
		}

		return `${maskedUsername}@${domain}`;
	};

	static formatPhoneNumber = number => {
		const phoneNumber = Util.getPhoneNumber(number),
			extension = Util.getExtension(number);
		let formattedPhoneNumber = '';

		if (phoneNumber.length > 0) {
			formattedPhoneNumber += `(${phoneNumber.substring(0, 3)}`;
		}
		if (phoneNumber.length > 3) {
			formattedPhoneNumber += `) ${phoneNumber.substring(3, 6)}`;
		}
		if (phoneNumber.length > 6) {
			formattedPhoneNumber += `-${phoneNumber.substring(6)}`;
		}

		return (formattedPhoneNumber += extension ? ` ext:${extension}` : '');
	};

	/**
	 * Format currency, OR unformat and reformat it if it's already formatted.
	 * @param num
	 * @returns {string}
	 */
	static formatCurrency = num => {
		/**
		 * Formats a value as a US currency string (e.g., "$1,234.56").
		 * Accepts numbers or strings, including those with currency symbols or commas.
		 * Returns "$0.00" for null, undefined, empty, Infinity, -Infinity, or invalid input.
		 *
		 * @param {number|string|null|undefined} num - The value to format as currency.
		 * @returns {string} The formatted currency string.
		 *
		 * @example
		 * Util.formatCurrency(1234.56); // "$1,234.56"
		 * Util.formatCurrency("$1,234.56"); // "$1,234.56"
		 * Util.formatCurrency(null); // "$0.00"
		 */
		if (num == null || num === '' || num === Infinity || num === -Infinity)
			return '$0.00';
		const cleaned = typeof num === 'string' ? num.replace(/[^\d.-]/g, '') : num;
		if (
			cleaned === '' ||
			cleaned === '-' ||
			cleaned === '.' ||
			cleaned === '-.'
		)
			return '$0.00';
		const value = Number(cleaned);
		if (!Number.isFinite(value)) return '$0.00';

		return value.toLocaleString('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});
	};

	static getFraudNumber = () => {
		return '888-434-0055';
	};

	static isInRoute = routeName => {
		const { pathname } = window.location;
		const routeNameRegex = new RegExp(`/${routeName}`, 'i');

		return routeNameRegex.test(pathname);
	};

	/**
	 * @param {object} object
	 * @param {object} initial
	 * @returns {object}
	 * This function returns an object that will find the differences between the two objects wether they are nested or not
	 */
	static differences = (object, initial) =>
		transform(object, function (result, value, key) {
			if (!isEqual(value, initial[key])) {
				result[key] =
					isObject(value) && isObject(initial[key])
						? Util.differences(value, initial[key])
						: value;
			}
		});

	/**
	 * @param {object} object
	 * @param {string} prefix
	 * @returns {array}
	 * This function takes in an object, can have nested object, and returns all of the keys. It can be used in combination with Util.differences.
	 */
	static convertObjectToArray = (object, prefix = '') =>
		Object.keys(object).reduce((res, index) => {
			if (Array.isArray(object[index])) {
				return res;
			} else if (typeof object[index] === 'object' && object[index] !== null) {
				return [
					...res,
					...Util.convertObjectToArray(object[index], `${prefix + index}.`),
				];
			} else {
				return [...res, `${prefix + index}`];
			}
		}, []);

	static formatFieldName = fieldName => {
		let newFieldName = '';

		for (let i = 0; i < fieldName.length; i++) {
			if (i === 0) {
				newFieldName += fieldName[i].toUpperCase();
			} else if (fieldName[i] === fieldName[i].toUpperCase()) {
				newFieldName += ` ${fieldName[i].toLowerCase()}`;
			} else {
				newFieldName += fieldName[i];
			}
		}

		return newFieldName;
	};

	static generateMonthArray = () =>
		Array.from({ length: 12 }).map((item, index) => ({
			label: index + 1,
			value: index + 1,
		}));

	static generateYearArray = length => {
		const currentDate = new Date();
		const currentYear = currentDate.getFullYear();
		return Array.from({ length }).map((item, index) => ({
			label: currentYear + index,
			value: currentYear + index,
		}));
	};

	static getOS(onlyOS = false) {
		const clientStrings = [
			{ s: 'Windows 10', r: /(Windows 10.0|Windows NT 10.0)/ },
			{ s: 'Windows 8.1', r: /(Windows 8.1|Windows NT 6.3)/ },
			{ s: 'Windows 8', r: /(Windows 8|Windows NT 6.2)/ },
			{ s: 'Windows 7', r: /(Windows 7|Windows NT 6.1)/ },
			{ s: 'Windows Vista', r: /Windows NT 6.0/ },
			{ s: 'Windows Server 2003', r: /Windows NT 5.2/ },
			{ s: 'Windows XP', r: /(Windows NT 5.1|Windows XP)/ },
			{ s: 'Windows 2000', r: /(Windows NT 5.0|Windows 2000)/ },
			{ s: 'Windows ME', r: /(Win 9x 4.90|Windows ME)/ },
			{ s: 'Windows 98', r: /(Windows 98|Win98)/ },
			{ s: 'Windows 95', r: /(Windows 95|Win95|Windows_95)/ },
			{ s: 'Windows NT 4.0', r: /(Windows NT 4.0|WinNT4.0|WinNT|Windows NT)/ },
			{ s: 'Windows CE', r: /Windows CE/ },
			{ s: 'Windows 3.11', r: /Win16/ },
			{ s: 'Android', r: /Android/ },
			{ s: 'Open BSD', r: /OpenBSD/ },
			{ s: 'Sun OS', r: /SunOS/ },
			{ s: 'Linux', r: /(Linux|X11)/ },
			{ s: 'iOS', r: /(iPhone|iPad|iPod)/ },
			{ s: 'Mac OS X', r: /Mac OS X/ },
			{ s: 'Mac OS', r: /(MacPPC|MacIntel|Mac_PowerPC|Macintosh)/ },
			{ s: 'QNX', r: /QNX/ },
			{ s: 'UNIX', r: /UNIX/ },
			{ s: 'BeOS', r: /BeOS/ },
			{ s: 'OS/2', r: /OS\/2/ },
			{
				s: 'Search Bot',
				r: /(nuhk|Googlebot|Yammybot|Openbot|Slurp|MSNBot|Ask Jeeves\/Teoma|ia_archiver)/,
			},
		];
		const nAgt = navigator.userAgent;
		let osVersion;
		let cs;
		let os;

		for (const id in clientStrings) {
			cs = clientStrings[id];

			if (cs.r.test(nAgt)) {
				os = cs.s;
				break;
			}
		}

		if (/Windows/.test(os)) {
			osVersion = /Windows (.*)/.exec(os)[1];
			os = 'Windows';
		} else {
			switch (os) {
				case 'Mac OS X':
					osVersion = /Mac OS X (10[._\d]+)/.exec(nAgt)[1];
					break;

				case 'Android':
					osVersion = /Android ([._\d]+)/.exec(nAgt)[1];
					break;

				case 'iOS':
					osVersion = /OS (\d+)_(\d+)_?(\d+)?/.exec(nAgt);
					osVersion = `${osVersion[1]}.${osVersion[2]}.${osVersion[3] | 0}`;
					break;
				default:
					break;
			}
		}

		if (onlyOS) {
			return os;
		}

		return `${os} ${osVersion}`;
	}

	// return user's browser information
	static getBrowser(onlyBrowser = false) {
		// browser
		const nAgt = navigator.userAgent;
		let browser = navigator.appName;
		let version = `${parseFloat(navigator.appVersion)}`;
		let majorVersion = parseInt(navigator.appVersion, 10);
		let nameOffset;
		let verOffset;
		let ix;

		// Opera
		if ((verOffset = nAgt.indexOf('Opera')) !== -1) {
			browser = 'Opera';
			version = nAgt.substring(verOffset + 6);

			if ((verOffset = nAgt.indexOf('Version')) !== -1) {
				version = nAgt.substring(verOffset + 8);
			}

			// Opera Next
		} else if ((verOffset = nAgt.indexOf('OPR')) !== -1) {
			browser = 'Opera';
			version = nAgt.substring(verOffset + 4);

			// Edge
		} else if ((verOffset = nAgt.indexOf('Edge')) !== -1) {
			browser = 'Microsoft Edge';
			version = nAgt.substring(verOffset + 5);

			// MSIE
		} else if ((verOffset = nAgt.indexOf('MSIE')) !== -1) {
			browser = 'Microsoft Internet Explorer';
			version = nAgt.substring(verOffset + 5);

			// Chrome
		} else if ((verOffset = nAgt.indexOf('Chrome')) !== -1) {
			browser = 'Chrome';
			version = nAgt.substring(verOffset + 7);

			// Safari
		} else if ((verOffset = nAgt.indexOf('Safari')) !== -1) {
			browser = 'Safari';
			version = nAgt.substring(verOffset + 7);

			if ((verOffset = nAgt.indexOf('Version')) !== -1) {
				version = nAgt.substring(verOffset + 8);
			}

			// Firefox
		} else if ((verOffset = nAgt.indexOf('Firefox')) !== -1) {
			browser = 'Firefox';
			version = nAgt.substring(verOffset + 8);

			// MSIE 11+
		} else if (nAgt.indexOf('Trident/') !== -1) {
			browser = 'Microsoft Internet Explorer';
			version = nAgt.substring(nAgt.indexOf('rv:') + 3);

			// Other browsers
		} else if (
			(nameOffset = nAgt.lastIndexOf(' ') + 1) <
			(verOffset = nAgt.lastIndexOf('/'))
		) {
			browser = nAgt.substring(nameOffset, verOffset);
			version = nAgt.substring(verOffset + 1);

			if (browser.toLowerCase() == browser.toUpperCase()) {
				browser = navigator.appName;
			}
		}

		// trim the version string
		if ((ix = version.indexOf(';')) != -1) {
			version = version.substring(0, ix);
		}
		if ((ix = version.indexOf(' ')) != -1) {
			version = version.substring(0, ix);
		}
		if ((ix = version.indexOf(')')) != -1) {
			version = version.substring(0, ix);
		}

		majorVersion = parseInt(`${version}`, 10);

		if (isNaN(majorVersion)) {
			version = `${parseFloat(navigator.appVersion)}`;
			majorVersion = parseInt(navigator.appVersion, 10);
		}

		if (onlyBrowser) {
			return browser;
		}

		return `${browser} ${majorVersion} (${version})`;
	}

	static getSupportEmail() {
		return 'https://www.stamps.com/support/contact-support';
	}

	/* eslint-disable no-bitwise */
	static hasBitSet = (capabilities, bitValue) =>
		Boolean(capabilities & bitValue);

	static setBit = (capabilities, bitValue) => capabilities | bitValue;

	static removeBit = (capabilities, bitValue) => capabilities & ~bitValue;

	/**
	 * Relay data to each target origin parameter.
	 */
	static postMessage = (data, targetOrigin = '') => {
		const targetArr = targetOrigin.split(',');

		for (const location of targetArr) {
			try {
				window.top.postMessage(data, location);
			} catch (error) {
				console.log(error);
			}
		}
	};

	static getCurrentDate() {
		const date = new Date();
		const month = date.getMonth() + 1;
		const day = date.getDate();

		return [
			date.getFullYear(),
			(month > 9 ? '' : '0') + month,
			(day > 9 ? '' : '0') + day,
		].join('-');
	}

	/**
	 * compare the order of two date strings
	 * @param {string} date1 format: MM/DD/YYYY
	 * @param {string} date2 format: MM/DD/YYYY
	 */
	static dateComparator = (date1, date2) => {
		const timestamp1 = this.dateToComparableTimestamp(date1);
		const timestamp2 = this.dateToComparableTimestamp(date2);

		// 0 means equivalent
		if (timestamp1 === null && timestamp2 === null) {
			return 0;
		}

		// negative when date1 occurs before date2
		if (timestamp1 === null) {
			return -1;
		}

		// postive when date1 occurs after date2
		if (timestamp2 === null) {
			return 1;
		}

		return timestamp1 - timestamp2;
	};

	/**
	 * convert a date string into a comparable timestamp
	 * @param {string} date format: MM/DD/YYYY
	 */
	static dateToComparableTimestamp = date => {
		if (date === undefined || date === null || date === '') {
			return null;
		}

		const timestamp = new Date(date).getTime(); // return NaN if the date is not valid

		return isNumber(timestamp) && isNaN(timestamp) === false ? timestamp : null;
	};

	/**
	 * Use this method to handle different cases between SERA APIs and JavaScript frontend.
	 * JavaScript frontend expects camelCase keys while SERA APIs typically returns in
	 * snake_case keys.
	 *
	 * Convert nested object keys in snake_case to camelCase, and return a new object. This
	 * method requires lodash methods like toPairs, fromPairs and camelCase.
	 * ex:
	 * {
	 * 		test_field_1: 1,
	 * 		test_field_2: [
	 * 			{ nested_field_1: 1 },
	 * 			{ nested_field_2: 2 },
	 * 		]
	 * }
	 *
	 * return:
	 * {
	 * 		testField1: 1,
	 * 		testField2: [
	 * 			{ nestedField1: 1 },
	 * 			{ nestedField12: 2 },
	 * 		]
	 *  }
	 *
	 * @param {*} data The object (or array of objects) with snake_case keys.
	 * @returns {object}
	 */
	static toCamelCase = data => {
		if (isArray(data)) {
			return data.map(obj => this.toCamelCase(obj));
		} else if (isObject(data)) {
			// first, create an array of enumerable string keyed-value pairs,
			// next, loop through the array and convert keys to camelCase, and
			// finally create a new object composed from key-value pairs
			return fromPairs(
				toPairs(data).map(([key, value]) => [
					camelCase(key),
					isArray(value) || isObject(value) ? this.toCamelCase(value) : value,
				])
			);
		}

		return data;
	};

	static getCarrierName = number => {
		const upsTrackingRegex = [
			new RegExp(
				/\b(1Z ?[0-9A-Z]{3} ?[0-9A-Z]{3} ?[0-9A-Z]{2} ?[0-9A-Z]{4} ?[0-9A-Z]{3} ?[0-9A-Z]|T\d{3} ?\d{4} ?\d{3})\b/i
			),
		];
		const uspsTrackingRegex = [
			new RegExp(
				/\b((420 ?\d{5} ?)?(91|92|93|94|95|01|03|04|70|23|13)\d{2} ?\d{4} ?\d{4} ?\d{4} ?\d{4}( ?\d{2,6})?)\b/i
			),
			new RegExp(
				/\b((M|P[A-Z]?|D[C-Z]|LK|E[A-C]|V[A-Z]|R[A-Z]|CP|CJ|LC|LJ) ?\d{3} ?\d{3} ?\d{3} ?[A-Z]?[A-Z]?)\b/i
			),
			new RegExp(/\b(82 ?\d{3} ?\d{3} ?\d{2})\b/i),
		];
		const dhlTrackingRegex = [
			new RegExp(
				/\b(\d{4}[- ]?\d{4}[- ]?\d{2}|\d{3}[- ]?\d{8}|[A-Z]{3}\d{7})\b/i
			),
		];
		const fedexTrackingRegex = [
			new RegExp(
				/\b(((96\d\d|6\d)\d{3} ?\d{4}|96\d{2}|\d{4}) ?\d{4} ?\d{4}( ?\d{3})?)\b/i
			),
		];

		if (number === '') {
			return null;
		}

		for (let index = 0; index < upsTrackingRegex.length; index++) {
			const regex = upsTrackingRegex[index];
			if (regex.test(number)) {
				return 'UPS';
			}
		}

		for (let index = 0; index < uspsTrackingRegex.length; index++) {
			const regex = uspsTrackingRegex[index];
			if (regex.test(number)) {
				return 'USPS';
			}
		}

		for (let index = 0; index < fedexTrackingRegex.length; index++) {
			const regex = fedexTrackingRegex[index];
			if (regex.test(number)) {
				return 'FedEx';
			}
		}

		for (let index = 0; index < dhlTrackingRegex.length; index++) {
			const regex = dhlTrackingRegex[index];
			if (regex.test(number)) {
				return 'DHL eCommerce';
			}
		}

		return 'Other';
	};

	static concatenateToAddress = toAddress => {
		const {
			name,
			companyName,
			addressLine1,
			addressLine2,
			addressLine3,
			city,
			stateProvince,
			postalCode,
			countryCode,
		} = toAddress;

		let address = '';

		address += name ? `${name}<br />` : '';
		address += companyName ? `${companyName}<br />` : '';
		address += addressLine1 ? `${addressLine1}<br />` : '';
		address += addressLine2 ? `${addressLine2}<br />` : '';
		address += addressLine3 ? `${addressLine3}<br />` : '';
		address += city ? `${city}, ` : '';
		address += stateProvince ? `${stateProvince} ` : '';
		address += postalCode ? `${postalCode}<br />` : '';
		address += countryCode && countryCode !== 'US' ? `${countryCode}` : '';

		return address;
	};

	static toQueryString = params =>
		Object.keys(params)
			.map(
				key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
			)
			.join('&');

	static formatAddressToOneLine = (address = {}, includeAddressLine) => {
		const {
			name = '',
			companyName = '',
			addressLine1 = '',
			addressLine2 = '',
			addressLine3 = '',
			city = '',
			postalCode = '',
			stateProvince = '',
		} = address;

		let formattedAddress = '';

		if (isString(name) && name !== '') {
			formattedAddress += `${name}, `;
		}

		if (isString(companyName) && companyName !== '') {
			formattedAddress += `${companyName}, `;
		}

		if (includeAddressLine) {
			if (isString(addressLine1) && addressLine1 !== '') {
				formattedAddress += ` ${addressLine1}`;
			}

			if (isString(addressLine2) && addressLine2 !== '') {
				formattedAddress += ` ${addressLine2}`;
			}

			if (isString(addressLine3) && addressLine3 !== '') {
				formattedAddress += ` ${addressLine3}`;
			}
		}

		if (isString(city) && city !== '') {
			formattedAddress += `, ${city}`;
		}

		if (isString(stateProvince) && stateProvince !== '') {
			formattedAddress += `, ${stateProvince}`;
		}

		if (isString(postalCode) && postalCode !== '') {
			formattedAddress += ` ${postalCode}`;
		}

		return formattedAddress;
	};

	/**
	 * Return a date range object containing 'dateRangeStart' and 'dateRangeEnd' properties.
	 *
	 * @param {DateRangeOption} option A selected date-range option such as "Past 7 Days".
	 * @returns {object}
	 */
	static calculateDateRange = option => {
		const now = new Date();
		const fullYear = now.getFullYear();
		const month = now.getMonth() + 1; // 0-11 + 1
		const date = now.getDate(); // 1-31
		const start = moment(`${fullYear}-${month}-${date}`, 'YYYY-MM-DD');
		const end = moment(`${fullYear}-${month}-${date}`, 'YYYY-MM-DD');
		const dateRange = {
			dateRangeStart: null,
			dateRangeEnd: null,
		};

		// a date-range option enum
		if (isString(option)) {
			switch (option) {
				case DateRange.Past7Days:
					start.subtract(7, 'days');
					break;
				case DateRange.Past30Days:
				case DateRange.Custom:
					start.subtract(30, 'days');
					break;
				case DateRange.Past3Months:
					start.subtract(3, 'month');
					break;
				case DateRange.Past6Months:
					start.subtract(6, 'month');
					break;
				case DateRange.Past12Months:
					start.subtract(12, 'month');
					break;
				case DateRange.Past2Years:
					start.subtract(2, 'year');
					break;
				case DateRange.All:
					// set to 2000-01-01
					start.year(2000);
					start.dayOfYear(1); // 1st day of the year of 2000
					break;
				case DateRange.Today:
				default:
					break;
			}

			dateRange.dateRangeStart = start.toDate();
			dateRange.dateRangeEnd = end.toDate();
		}

		return dateRange;
	};

	static getSharedItemsFromArray = arrays => {
		if (arrays.length === 0) {
			return [];
		}

		const sharedItems = arrays.reduce((accumulator, currentArray) => {
			if (!currentArray) {
				currentArray = [];
			}
			return accumulator.filter(value => {
				return currentArray.includes(value);
			});
		});

		return sharedItems;
	};

	static getSharedObjectsFromArray = (arrays, property) => {
		if (arrays.length === 0 || arrays.every(item => item === null)) {
			return [];
		}

		const sharedItems = arrays.reduce((accumulator, currentArray) => {
			return (
				accumulator &&
				accumulator.filter(
					value =>
						currentArray &&
						currentArray.some(item => item[property] === value[property])
				)
			);
		}, arrays[0]);

		return sharedItems;
	};

	/**
	 * Check if the filename is of a specific type.
	 *
	 * @param {string} file Name of the file to be checked.
	 * @param {array} fileTypes Array of file types to be checked against.
	 * @returns {boolean}
	 */
	static isFileType = (fileName = '', fileTypes = []) => {
		for (const type of fileTypes) {
			if (fileName.toLowerCase().endsWith(type.toLowerCase())) {
				return true;
			}
		}

		return false;
	};

	static openHelpCenter = (page = '') => {
		const { HelpCenterBaseUrl } = Session.getSettings();

		window.open(`${HelpCenterBaseUrl}/hc/en-us/articles/${page}`, '_blank');
	};

	/**
	 * Function to get ParcelGuard claim URL and open it in a new tab
	 * @param {string} universalPostageId - The universal postage ID for the claim
	 * @param {function} showError - Function to show error modal
	 * @returns {Promise<void>}
	 */
	static openParcelGuardClaimForm = async (universalPostageId, showError) => {
		if (!universalPostageId) {
			console.error(
				'universalPostageId is required to open ParcelGuard claim form'
			);
			return;
		}

		try {
			const response = await Communication.getUrl({
				urlType: 'ParcelGuardClaim',
				applicationContext: universalPostageId,
			});

			if (Communication.isSuccessful(response)) {
				// Open the claim form URL in a new tab
				window.open(response.data, '_blank');

				// Track successful analytics event
				Analytics.trackEvent(
					'fileInsuranceClaim',
					{
						insurance_type: 'ParcelGuard',
						success: true,
					},
					Analytics.agent.segment
				);
			} else {
				// Track failed analytics event
				Analytics.trackEvent(
					'fileInsuranceClaim',
					{
						insurance_type: 'ParcelGuard',
						success: false,
						error: response,
					},
					Analytics.agent.segment
				);

				// Show error modal
				showError({
					response,
				});
			}
		} catch (error) {
			console.error('Error opening ParcelGuard claim form:', error);

			// Track error analytics event
			Analytics.trackEvent(
				'fileInsuranceClaim',
				{
					insurance_type: 'ParcelGuard',
					success: false,
					error: error.message,
				},
				Analytics.agent.segment
			);

			// Show error modal
			showError({
				data: {
					title: 'Error',
					description:
						'Failed to open ParcelGuard claim form. Please try again.',
					primaryButtonText: 'Close',
					handleType: 'custom',
				},
			});
		}
	};

	/**
	 * Determines if contact information changes require verification
	 * @param {string} params.oldPhoneNumber - current phone number
	 * @param {string} params.newPhoneNumber - new phone number
	 * @param {string} params.oldEmail - current email address
	 * @param {string} params.newEmail - new email address
	 * @returns {Object} Object containing isEmailChanged and isPhoneNumberChanged flags
	 */
	static checkContactChangesRequireVerification = ({
		oldPhoneNumber,
		newPhoneNumber,
		oldEmailAddress,
		newEmailAddress,
	}) => {
		const PUBLIC_EMAIL_DOMAINS = [
			'gmail.com',
			'outlook.com',
			'yahoo.com',
			'zoho.com',
			'protonmail.com',
			'proton.me',
			'icloud.com',
			'me.com',
			'tutamail.com',
			'tuta.io',
			'aol.com',
			'mail.com',
			'neo.mailbox.org',
			'gmx.com',
			'gmx.us',
			'yandex.com',
			'mailfence.com',
			'fastmail.com',
			'titan.email',
		];

		const isPhoneNumberChanged = newPhoneNumber !== oldPhoneNumber;

		// check if email has changed
		const oldEmail = oldEmailAddress?.toLowerCase();
		const newEmail = newEmailAddress?.toLowerCase();
		const isEmailChanged = oldEmail !== newEmail;

		if (!isEmailChanged) {
			return {
				isEmailChanged: false,
				isPhoneNumberChanged,
			};
		}

		// get the domains from each email
		const oldDomain = oldEmail?.split('@')[1];
		const newDomain = newEmail?.split('@')[1];

		// if the email domains match and the old email domain is not a public email domain, don't require verification
		const isSameDomain = oldDomain === newDomain;
		const isPublicDomain = PUBLIC_EMAIL_DOMAINS.includes(oldDomain);

		return {
			isEmailChanged: !isSameDomain || isPublicDomain,
			isPhoneNumberChanged,
		};
	};

	/**
	 * Checks if the current hostname is an Endicia domain
	 * @param {string} hostname - Optional hostname to check (defaults to window.location.hostname)
	 * @returns {boolean} True if hostname is an Endicia domain
	 */
	static isEndiciaDomain = (hostname = window.location.hostname) => {
		return (
			hostname === 'endicia.com' ||
			hostname === 'endicia.dev' ||
			hostname.endsWith('.endicia.com') ||
			hostname.endsWith('.endicia.dev')
		);
	};

	/**
	 * Redirects from Endicia domain to Stamps domain, preserving all query parameters
	 * @param {string} hostname - Optional hostname (defaults to window.location.hostname)
	 */
	static redirectEndiciaToStamps = (hostname = window.location.hostname) => {
		const newDomain = hostname
			.replace('endicia.com', 'stamps.com')
			.replace('endicia.dev', 'stamps.dev');
		const newUrl = window.location.href.replace(hostname, newDomain);
		window.location.replace(newUrl);
	};

	static getAgeOfAccountInDays = createdDate => {
		if (!createdDate) return null;
		const created = new Date(createdDate);
		const now = new Date();
		created.setHours(0, 0, 0, 0);
		now.setHours(0, 0, 0, 0);
		const diffTime = now - created;
		const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
		return diffDays;
	};
}

export default Util;
